import { useMemo } from 'react';
import { HeartHandshake } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/Skeleton';
import { MetricCard } from '@/components/financials/MetricCard';
import { QuietEmpty, WorkerChip } from './shared';
import { useMoraleTrend, useWorkCheckins } from '@/features/workMode/hooks';
import { moodMeta, moodTone } from '@/features/workMode/labels';
import type { WorkLookups } from '@/features/workMode/useLookups';
import { formatDateOnly } from '@/lib/formatters';

interface CheckinsTabProps {
  lookups: WorkLookups;
}

function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * "How do you feel today?" over time. Aggregates first — the trend is the
 * point — then the individual (non-private) notes so a rough week has a face.
 */
export function CheckinsTab({ lookups }: CheckinsTabProps) {
  const { data: trend = [], isLoading: trendLoading } = useMoraleTrend(30);
  const { data: checkins = [], isLoading: listLoading } = useWorkCheckins(14);

  const summary = useMemo(() => {
    let responses = 0;
    let weighted = 0;
    let low = 0;
    for (const d of trend) {
      responses += d.responses;
      weighted += d.avg_mood * d.responses;
      low += d.low_count;
    }
    const last7 = trend.slice(-7);
    const prev7 = trend.slice(-14, -7);
    const avg = (rows: typeof trend) => {
      const n = rows.reduce((a, r) => a + r.responses, 0);
      return n ? rows.reduce((a, r) => a + r.avg_mood * r.responses, 0) / n : null;
    };
    const a7 = avg(last7);
    const p7 = avg(prev7);
    return {
      responses,
      avg: responses ? weighted / responses : null,
      low,
      delta: a7 != null && p7 != null && p7 > 0 ? (a7 - p7) / p7 : null,
      days: trend.filter((d) => d.responses > 0).length,
    };
  }, [trend]);

  // Fill a 30-day axis so quiet days show as gaps rather than vanishing.
  const days = useMemo(() => {
    const byDay = new Map(trend.map((d) => [d.day.slice(0, 10), d]));
    const out: Array<{ key: string; label: string; avg: number | null; responses: number; low: number }> = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = localDateKey(d);
      const row = byDay.get(key);
      out.push({
        key,
        label: d.toLocaleDateString([], { month: 'short', day: 'numeric' }),
        avg: row ? row.avg_mood : null,
        responses: row?.responses ?? 0,
        low: row?.low_count ?? 0,
      });
    }
    return out;
  }, [trend]);

  const withNotes = checkins.filter((c) => !c.is_private);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Average mood (30d)" value={summary.avg == null ? '—' : `${summary.avg.toFixed(1)} / 5`} delta={summary.delta} deltaLabel="vs. prior week" loading={trendLoading} icon={<HeartHandshake className="h-4 w-4" />} />
        <MetricCard label="Check-ins" value={String(summary.responses)} hint={`${summary.days} day${summary.days === 1 ? '' : 's'} with responses`} loading={trendLoading} tone="neutral" />
        <MetricCard label="Rough days reported" value={String(summary.low)} hint="Mood of 1 or 2" loading={trendLoading} tone={summary.low > 0 ? 'negative' : 'positive'} />
        <MetricCard label="Today" value={String(trend.find((d) => d.day.slice(0, 10) === localDateKey(new Date()))?.responses ?? 0)} hint="People who checked in" loading={trendLoading} tone="info" />
      </div>

      <section className="rounded-2xl border border-border bg-card p-4">
        <h3 className="text-sm font-medium text-foreground">Last 30 days</h3>
        {trendLoading ? (
          <Skeleton className="mt-4 h-40" />
        ) : (
          <div className="mt-4 flex h-44 items-end gap-1">
            {days.map((d) => (
              <div key={d.key} className="group relative flex flex-1 flex-col items-center justify-end" title={`${d.label}: ${d.avg == null ? 'no check-ins' : `${d.avg.toFixed(1)} / 5 from ${d.responses}`}`}>
                <div
                  className={cn('w-full rounded-t-sm transition-colors', d.avg == null ? 'bg-border/60' : moodTone(d.avg))}
                  style={{ height: d.avg == null ? 4 : `${(d.avg / 5) * 100}%` }}
                />
                <span className="mt-1 text-[10px] tabular-nums text-muted-foreground">{d.responses || ''}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
          <span>{days[0]?.label}</span>
          <span>{days[days.length - 1]?.label}</span>
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card">
        <header className="border-b border-border px-4 py-3">
          <h3 className="text-sm font-medium text-foreground">Recent check-ins</h3>
        </header>
        {listLoading ? (
          <Skeleton className="m-4 h-32" />
        ) : withNotes.length === 0 ? (
          <div className="p-4">
            <QuietEmpty>No check-ins yet.</QuietEmpty>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {withNotes.map((c) => {
              const m = moodMeta(c.mood);
              return (
                <li key={c.id} className="flex items-start gap-3 px-4 py-3">
                  <span className="text-2xl leading-none" aria-label={m.label} title={m.label}>
                    {m.emoji}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <WorkerChip person={lookups.person(c.user_id)} size={24} />
                      <span className="shrink-0 text-xs text-muted-foreground">{formatDateOnly(c.checkin_date)}</span>
                    </div>
                    <p className={cn('mt-1 text-sm', c.note ? 'text-foreground' : 'text-muted-foreground')}>{c.note || `Feeling ${m.label.toLowerCase()}.`}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
